"use client";

import { LineChart, Line } from "@/components/charts/line-chart";
import { Grid } from "@/components/charts/grid";
import { XAxis } from "@/components/charts/x-axis";
import { YAxis } from "@/components/charts/y-axis";
import { ChartTooltip } from "@/components/charts/tooltip";
import {
  ProfitLossLine,
  profitLossColor,
  resolveProfitLossTooltipLabel,
} from "@/components/charts/profit-loss-line";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import {
  ChartAxisFrame,
  ChartXAxisRow,
} from "@/components/charts/chart-axis-frame";
import {
  Legend,
  LegendItem,
  LegendLabel,
  LegendMarker,
} from "@/components/charts/legend";
import { ProfitLossLegendHoverProvider } from "@/components/charts/profit-loss-legend-hover";
import { resolveCurve, type CurveName } from "@/components/charts/curve-map";
import { useDismiss } from "@/lib/hooks/use-dismiss";
import { useLegendHoverSelect } from "@/lib/hooks/use-hover-select";
import { useViewerLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { ModuleChartProfitLossData } from "@/types/modules";
import { chartGrayscaleStyle } from "../imports-token-scope";
import { JSX, useCallback, useMemo, useRef } from "react";
import {
  moduleAxisLabels,
} from "./chart-module-metrics";
import { CHART_PLOT_HEIGHT, MIN_CHART_WIDTH, MIN_PX_PER_POINT } from "@/config/layout";

const DEFAULT_DATA: ModuleChartProfitLossData = {
  points: [
    { date: "2025-01-01", value: 420 },
    { date: "2025-01-02", value: -180 },
    { date: "2025-01-03", value: 260 },
    { date: "2025-01-04", value: -95 },
    { date: "2025-01-05", value: 340 },
    { date: "2025-01-06", value: 180 },
    { date: "2025-01-07", value: -240 },
    { date: "2025-01-08", value: 520 },
  ],
};

const CHART_HEIGHT = CHART_PLOT_HEIGHT.profitLoss;
const PX_PER_POINT = MIN_PX_PER_POINT.profitLoss;

export default function ModuleProfitLossChart({
  data = DEFAULT_DATA,
  xAxisLabel,
  yAxisLabel,
  showMarkers = false,
  curve = "linear",
  grayscale,
  className,
}: {
  data?: ModuleChartProfitLossData;
  xAxisLabel?: string;
  yAxisLabel?: string;
  showMarkers?: boolean;
  curve?: CurveName;
  grayscale?: boolean;
  className?: string;
}): JSX.Element {
  const { t } = useViewerLanguage();
  const chartData = useMemo(
    () =>
      data.points.map((p) => ({
        date: new Date(p.date),
        pnl: p.value,
      })),
    [data.points],
  );

  const legend = useLegendHoverSelect();
  const { setSelected: setLegendSelected } = legend;
  const legendRef = useRef<HTMLDivElement>(null);
  const clearLegend = useCallback(
    () => setLegendSelected(null),
    [setLegendSelected],
  );
  useDismiss(legend.selected !== null, clearLegend, legendRef);

  return (
    <div className={cn("flex w-full flex-col gap-3", className)} style={chartGrayscaleStyle(grayscale)}>
      <ChartAxisFrame {...moduleAxisLabels({ xAxisLabel, yAxisLabel })}>
      <SwipeScroll className="min-w-0 flex-1 pb-1 [mask-image:linear-gradient(to_right,black_0%,black_95%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_right,black_0%,black_95%,transparent_100%)]">
      <div
        style={{
          minWidth: Math.max(MIN_CHART_WIDTH, chartData.length * PX_PER_POINT),
        }}
      >
      <div style={{ height: CHART_HEIGHT }}>
        <ProfitLossLegendHoverProvider hoveredIndex={legend.effective}>
        <LineChart
          data={chartData}
          xDataKey="date"
          margin={{ top: 20, right: 24, bottom: 8, left: 0 }}
          aspectRatio="auto"
          className="h-full"
        >
          <Grid highlightRowValues={[0]} horizontal />
          <Line
            curve={resolveCurve(curve)}
            dataKey="pnl"
            fadeEdges={false}
            showHighlight={false}
            showMarkers={showMarkers}
            stroke="transparent"
            strokeWidth={0}
          />
          <ProfitLossLine
            dataKey="pnl"
            positiveColor={data.positiveColor ?? "var(--color-emerald-500)"}
            negativeColor={data.negativeColor ?? "var(--color-red-500)"}
          />
          <XAxis />
          <YAxis />
          <ChartTooltip
            showDatePill
            indicatorColor={(point) =>
              profitLossColor((point.pnl as number) ?? 0)
            }
            rows={(point) => {
              const value = (point.pnl as number) ?? 0;
              return [
                {
                  color: profitLossColor(value),
                  label: resolveProfitLossTooltipLabel(t("Profit/Loss")),
                  value,
                },
              ];
            }}
          />
        </LineChart>
        </ProfitLossLegendHoverProvider>
      </div>
      <ChartXAxisRow />
      </div>
      </SwipeScroll>
      </ChartAxisFrame>

      <SwipeScroll ref={legendRef} className="w-full pb-1" fade="auto">
        <Legend
          hoveredIndex={legend.hovered}
          onHoverChange={legend.setHovered}
          selectedIndex={legend.selected}
          onSelect={legend.select}
          onLabelRef={legend.setLabelRef}
          items={[
            {
              label: t("Profit (positive)"),
              value: 0,
              color: data.positiveColor ?? "var(--color-emerald-500)",
            },
            {
              label: t("Loss (negative)"),
              value: 0,
              color: data.negativeColor ?? "var(--color-red-500)",
            },
          ]}
          className="flex flex-row gap-6"
        >
          <LegendItem className="flex shrink-0 items-center gap-2">
            <LegendMarker className="h-2.5 w-2.5" />
            <LegendLabel />
          </LegendItem>
        </Legend>
      </SwipeScroll>
    </div>
  );
}
